/**
 * The AI feature builder (ADR 0025): describe a feature, Claude writes a plugin,
 * you see a live preview with your data (changes only described), the
 * permissions and the code, then install it — or ask for changes.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { AiError } from '@nexus/ai';
import {
  buildPlugin,
  EMPTY_BUILDER,
  refineRequest,
  savePlugin,
  type BuilderState,
  type InstalledPlugin,
  type PluginProposal,
} from '@nexus/plugins';
import { Dialog } from '../components/Dialog';
import { useStore } from '../db/context';
import { transport, useAiChat } from '../ai/chat';
import { AiNotReady } from '../ai/AiNotReady';
import { routeLabel, useAiReady } from '../ai/providers';
import { useUi } from '../state/ui';
import { useBuilder } from './builderState';
import { checkPlugin } from './editorState';
import { PluginSurface } from './PluginSurface';
import { unloadPlugin } from './runtime';

export function BuilderDialog() {
  const { t } = useTranslation();
  const target = useBuilder((s) => s.target);
  const close = useBuilder((s) => s.close);
  return (
    <Dialog
      open={target !== undefined}
      onOpenChange={(o) => !o && close()}
      title={
        target
          ? t('plugins.builder.refineTitle', { name: target.manifest.name })
          : t('plugins.builder.title')
      }
      wide
    >
      {target !== undefined && (
        <Builder key={target?.pageId ?? 'new'} target={target} onDone={close} />
      )}
    </Dialog>
  );
}

let previewSeq = 0;

function Builder({ target, onDone }: { target: InstalledPlugin | null; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const readiness = useAiReady('plugin');
  const [request, setRequest] = useState('');
  const [state, setState] = useState<BuilderState>(EMPTY_BUILDER);
  const [proposal, setProposal] = useState<(PluginProposal & { previewId: string }) | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCode, setShowCode] = useState(false);
  const [cost, setCost] = useState<number>(0);

  const ask = async () => {
    const text = request.trim();
    if (!text) return;
    setBusy('');
    setError(null);
    try {
      const first = state.messages.length === 0 && target;
      const { proposal: p, state: next } = await buildPlugin({
        transport,
        model: 'sonnet',
        state,
        request: first ? refineRequest(target, text) : text,
        check: checkPlugin,
        onText: (soFar) => setBusy(soFar),
        onUsage: (usage) => {
          setCost((c) => c + usage.costUsd);
          useAiChat.setState({ usage });
        },
      });
      setState(next);
      setProposal({ ...p, previewId: `preview-${++previewSeq}` });
      setRequest('');
      setShowCode(false);
    } catch (e) {
      setError(
        e instanceof AiError && e.code !== 'api'
          ? t(`ai.errors.${e.code}`)
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setBusy(null);
    }
  };

  const install = async () => {
    if (!proposal) return;
    const saved = await savePlugin(store, {
      manifest: proposal.manifest,
      code: proposal.code,
      source: 'ai',
      request: state.messages.length ? firstRequest(state) : undefined,
      enabled: true,
    });
    unloadPlugin(saved.pageId);
    useUi.getState().open({ kind: 'plugins' });
    onDone();
  };

  if (readiness.status !== 'ready') return <AiNotReady status={readiness.status} />;

  // A not-yet-installed plugin, for the preview (changes are only described).
  const preview: InstalledPlugin | null = proposal
    ? {
        pageId: target?.pageId ?? proposal.previewId,
        manifest: proposal.manifest,
        code: proposal.code,
        enabled: true,
        source: 'ai',
        requests: [],
        updatedAt: Number(proposal.previewId.slice(8)),
      }
    : null;
  const m = proposal?.manifest;
  const usd = (n: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(n);

  return (
    <div className="flex flex-col gap-4 text-sm" data-testid="plugin-builder">
      {proposal && m && preview && (
        <section className="flex flex-col gap-3 rounded-lg border border-accent/40 p-3">
          <div>
            <h3 className="font-semibold">
              {m.name} <span className="text-xs font-normal text-fg-muted">v{m.version}</span>
            </h3>
            <p>{proposal.summary}</p>
            {proposal.notes && <p className="mt-1 text-xs text-fg-muted">{proposal.notes}</p>}
          </div>
          <div>
            <h4 className="mb-1 text-xs font-medium tracking-wide text-fg-muted uppercase">
              {t('plugins.builder.permissions')}
            </h4>
            <ul className="list-inside list-disc text-xs">
              {!m.permissions.read && !m.permissions.write && !m.permissions.network.length && (
                <li>{t('plugins.builder.noPermissions')}</li>
              )}
              {m.permissions.read && <li>{t('plugins.builder.permRead')}</li>}
              {m.permissions.write && <li>{t('plugins.builder.permWrite')}</li>}
              {m.permissions.network.map((d) => (
                <li key={d}>{t('plugins.builder.permNetwork', { domain: d })}</li>
              ))}
            </ul>
          </div>
          {[
            ...m.blocks.map((b) => ({ kind: 'block' as const, ...b })),
            ...m.views.map((v) => ({ kind: 'view' as const, ...v })),
          ].map((x) => (
            <div key={`${x.kind}:${x.id}`} className="rounded-lg border border-border p-2">
              <p className="mb-2 text-xs text-fg-muted">
                {t('plugins.builder.preview', { title: x.title })}
              </p>
              <PluginSurface plugin={preview} kind={x.kind} id={x.id} dryRun />
            </div>
          ))}
          {(m.commands.length > 0 || m.automations.length > 0) && (
            <ul className="list-inside list-disc text-xs text-fg-muted">
              {m.commands.map((c) => (
                <li key={c.id}>{t('plugins.adds.command', { title: c.title })}</li>
              ))}
              {m.automations.map((a) => (
                <li key={a.id}>{t('plugins.adds.automation', { title: a.title })}</li>
              ))}
            </ul>
          )}
          <details open={showCode} onToggle={(e) => setShowCode(e.currentTarget.open)}>
            <summary className="cursor-pointer text-xs text-fg-muted">
              {t('plugins.builder.showCode')}
            </summary>
            <pre className="mt-2 max-h-80 overflow-auto rounded-md bg-bg-subtle p-2 text-xs">
              {JSON.stringify(m, null, 2)}
              {'\n\n'}
              {proposal.code}
            </pre>
          </details>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void install()}
              className="rounded-full bg-accent px-3 py-1.5 font-medium text-accent-fg"
            >
              {target ? t('plugins.builder.update') : t('plugins.builder.install')}
            </button>
          </div>
        </section>
      )}

      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void ask();
        }}
      >
        <label className="flex flex-col gap-1">
          {proposal || target ? t('plugins.builder.changeLabel') : t('plugins.builder.askLabel')}
          <textarea
            autoFocus
            rows={3}
            value={request}
            onChange={(e) => setRequest(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void ask();
            }}
            placeholder={t('plugins.builder.placeholder')}
            className="rounded-lg border border-border bg-bg px-2 py-1.5"
          />
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-fg-muted">{routeLabel(readiness.route)}</span>
          {cost > 0 && (
            <span className="text-xs text-fg-muted">
              {t('plugins.builder.cost', { cost: usd(cost) })}
            </span>
          )}
          <span className="flex-1" />
          <button
            type="submit"
            disabled={busy !== null || !request.trim()}
            className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 hover:bg-bg-hover disabled:opacity-50"
          >
            <Sparkles className="size-4" />
            {proposal ? t('plugins.builder.change') : t('plugins.builder.build')}
          </button>
        </div>
      </form>
      {busy !== null && (
        <p role="status" className="text-xs text-fg-muted">
          {t('plugins.builder.working')}
          {busy ? ` ${busy.slice(-200)}` : ''}
        </p>
      )}
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** The user's first request in the conversation (kept with the plugin). */
function firstRequest(state: BuilderState): string {
  const first = state.messages[0];
  const text = typeof first?.content === 'string' ? first.content : '';
  const m = /Request: ([\s\S]*)$/.exec(text);
  return (m ? m[1]! : text).slice(0, 2000);
}
